import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {useApi} from '../hooks/useApi'
import {RootState} from './rootReducer'
import {LoadingStatuses} from '../components/types/LoadingStatusses'
import {AuthContextInterface} from '../components/AuthProvider'
import convertDuration from '../utils/convertDuration'

//#region types
export type StudyGuide = {
  id: string
  courseId: string
  lessonPlanId: string
  title: string
  timeWindow: number
  version: number
  status: string
  duration?: number
  description?: string
  completeDays?: number
  inProgress?: boolean
  isComplete?: boolean
  assets?: StudyGuideStudyDays[]
  courseTitle?: string
  abbreviation: 'PcM' | 'PjM' | 'PA' | 'PPD' | 'PDD' | 'CE'
  hasProgress?: boolean
}

export type StudyGuideStudyDays = {
  studyDay: number
  duration?: number
  isComplete?: boolean
  isCompleteOpen?: boolean
  inProgress?: boolean
  isBreakDay?: boolean
  isExamDay?: boolean
  video: StudyGuideAssets[]
  quiz: StudyGuideAssets[]
  flashcard_deck: StudyGuideAssets[]
  lecture_slides: StudyGuideAssets[]
  practice_exam: StudyGuideAssets[]
  break: StudyGuideAssets[]
}

export type StudyGuideAssets = {
  id: string
  assetGlobalId: number
  displayOrder: number
  studyDay: number
  title: string
  description: string
  assetType: string
  duration?: string
  questions?: number
  cards?: number
  downloadUrl?: string
  isComplete?: boolean
  assetTitle?: string
}

type SliceState = {
  studyGuide?: StudyGuide
  studyGuideStatus: LoadingStatuses
  error: string | null | undefined
}
//#endregion

//#region api
type SaveStudyGuideProgressPayload = {
  auth: AuthContextInterface
  globalAssetId: number
  studyDay?: number
}
export const saveStudyGuideProgress = createAsyncThunk<
  any,
  SaveStudyGuideProgressPayload
>('study-guide/progress/save', async ({auth, globalAssetId}) => {
  const endpoint = `/v1/are/study-guides/progress`

  const body = {
    globalAssetId,
    completed: true,
  }

  return useApi(auth, endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
  }).then(res => res.text())
})

type StartStudyGuidePayload = {
  auth: AuthContextInterface
  studyGuideId: string
  courseId: string
  migrate?: boolean
  clear?: boolean
}
export const startStudyGuide = createAsyncThunk<any, StartStudyGuidePayload>(
  'study-guide/start',
  async ({auth, studyGuideId, courseId, migrate, clear}) => {
    const endpoint = `/v1/are/study-guides/start/${studyGuideId}`

    return useApi(auth, endpoint, {
      method: 'POST',
      body: JSON.stringify({courseId, migrate, clear}),
    }).then(res => res.text())
  },
)

type GetStudyGuidePayload = {
  auth: AuthContextInterface
  studyGuideId: string
  studyDay?: number
  keepState?: boolean
}
export const getStudyGuide = createAsyncThunk<any, GetStudyGuidePayload>(
  'study-guide/get',
  async ({auth, studyGuideId, studyDay}) => {
    const endpoint = `/v1/are/study-guides/${studyGuideId}/${studyDay}`

    return useApi(auth, endpoint, {
      method: 'GET',
    }).then(res => res.json())
  },
)
//#endregion

//#region slice
const initialState: SliceState = {
  studyGuide: undefined,
  studyGuideStatus: LoadingStatuses.Idle,
  error: undefined,
}

export default createSlice({
  name: 'studyGuide',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(getStudyGuide.fulfilled, (state, action) => {
      const studyGuide = action.payload
      let duration = 0

      studyGuide.assets = studyGuide.assets.map((asset: StudyGuideAssets) => {
        let assetDuration = 0
        if (asset.assetType === 'video') {
          assetDuration =
            assetDuration + (convertDuration(asset.duration ?? '') ?? 0)
        } else if (asset.assetType === 'flashcard_deck') {
          assetDuration = assetDuration + (asset.cards ?? 0) * 120
        } else if (asset.assetType === 'quiz') {
          assetDuration = assetDuration + (asset.questions ?? 0) * 60
        } else if (asset.assetType === 'lecture_slides') {
          assetDuration = assetDuration + 300
        } else if (asset.assetType === 'practice_exam') {
          assetDuration = assetDuration + (Number(asset.duration) ?? 0)
        }

        duration = duration + assetDuration
        return {...asset, duration: assetDuration}
      })

      studyGuide.duration = duration

      studyGuide.assets = organizeAssetsWithAdditionalProps(
        action.payload.assets,
      )

      state.studyGuide = studyGuide
      state.studyGuideStatus = LoadingStatuses.Succeeded
    })
    builder.addCase(getStudyGuide.pending, (state, action) => {
      if (!action.meta.arg.keepState) {
        state.studyGuide = undefined
        state.studyGuideStatus = LoadingStatuses.Loading
      }
    })
    builder.addCase(startStudyGuide.fulfilled, state => {
      state.studyGuide = undefined
      state.studyGuideStatus = LoadingStatuses.Loading
    })
    builder.addCase(saveStudyGuideProgress.fulfilled, (state, action) => {
      if (state.studyGuide) {
        const studyGuide = {...state.studyGuide}
        studyGuide.assets = studyGuide.assets?.map(assets => {
          const updatedVideos = assets.video?.map(asset => {
            if (asset.assetGlobalId === action.meta.arg.globalAssetId) {
              return {...asset, isComplete: true}
            }

            return {...asset}
          })

          const updatedQuizzes = assets.quiz?.map(asset => {
            if (asset.assetGlobalId === action.meta.arg.globalAssetId) {
              return {...asset, isComplete: true}
            }

            return {...asset}
          })

          const updatedFlashcards = assets.flashcard_deck?.map(asset => {
            if (asset.assetGlobalId === action.meta.arg.globalAssetId) {
              return {...asset, isComplete: true}
            }

            return {...asset}
          })

          const updatedPracticeExams = assets.practice_exam?.map(asset => {
            if (asset.assetGlobalId === action.meta.arg.globalAssetId) {
              return {...asset, isComplete: true}
            }

            return {...asset}
          })

          const updatedBreaks = assets.break?.map(asset => {
            if (asset.assetGlobalId === action.meta.arg.globalAssetId) {
              return {...asset, isComplete: true}
            }

            return {...asset}
          })

          const updatesSlides = assets.lecture_slides?.map(asset => {
            if (asset.assetGlobalId === action.meta.arg.globalAssetId) {
              return {...asset, isComplete: true}
            }

            return {...asset}
          })

          return {
            ...assets,
            video: updatedVideos,
            quiz: updatedQuizzes,
            flashcard_deck: updatedFlashcards,
            lecture_slides: updatesSlides,
            practice_exam: updatedPracticeExams,
            break: updatedBreaks,
          }
        })

        studyGuide.assets = studyGuide.assets?.map(assets => {
          let isDayComplete = assets.isComplete
          if (assets.studyDay === action.meta.arg.studyDay) {
            const unifiedAssets = assets
              ? [
                  ...(Array.isArray(assets.video) ? assets.video : []),
                  ...(Array.isArray(assets.quiz) ? assets.quiz : []),
                  ...(Array.isArray(assets.flashcard_deck)
                    ? assets.flashcard_deck
                    : []),
                  ...(Array.isArray(assets.lecture_slides)
                    ? assets.lecture_slides
                    : []),
                  ...(Array.isArray(assets.practice_exam)
                    ? assets.practice_exam
                    : []),
                  ...(Array.isArray(assets.break) ? assets.break : []),
                ]
              : []

            isDayComplete =
              unifiedAssets.filter(ua => !ua.isComplete).length === 0
          }

          return {
            ...assets,
            isComplete: isDayComplete,
            isCompleteOpen: isDayComplete,
          }
        })

        state.studyGuide = studyGuide
      }
    })
  },
})
//#endregion

function calculateTotalDuration(assets: StudyGuideAssets[]): number {
  return assets.reduce((total, asset) => {
    if (asset.duration) {
      total += Number(asset.duration)
    }
    return total
  }, 0)
}

function organizeAssetsWithAdditionalProps(assets: StudyGuideAssets[]) {
  const grouped = assets.reduce(
    (result, asset) => {
      if (!result[asset.studyDay]) {
        result[asset.studyDay] = {}
      }

      if (!result[asset.studyDay][asset.assetType]) {
        result[asset.studyDay][asset.assetType] = []
      }

      if (asset.assetType === 'lesson_header') {
        asset.isComplete = true
      }

      result[asset.studyDay][asset.assetType].push(asset)
      return result
    },
    {} as Record<number, Record<string, StudyGuideAssets[]>>,
  )

  const result = Object.keys(grouped).map(studyDayKey => {
    const studyDayAssets = grouped[Number(studyDayKey)]

    const totalDuration = Object.values(studyDayAssets).reduce(
      (sum, assets) => sum + calculateTotalDuration(assets),
      0,
    )

    const allComplete = Object.values(studyDayAssets).every(assets =>
      assets.every(asset => asset.isComplete),
    )

    const isBreakDay = Object.values(studyDayAssets).every(assets =>
      assets.every(
        asset =>
          asset.assetType === 'break' && !asset.assetTitle?.includes('TEST'),
      ),
    )

    const isExamDay = Object.values(studyDayAssets).every(assets =>
      assets.every(asset => asset.assetType === 'practice_exam'),
    )

    return {
      studyDay: Number(studyDayKey),
      ...studyDayAssets,
      duration: totalDuration,
      isComplete: allComplete,
      isBreakDay,
      isExamDay,
    }
  })

  const inProgress = result.find(studyDay => !studyDay.isComplete)?.studyDay

  return result.map(studyDay => ({
    ...studyDay,
    inProgress: studyDay.studyDay === inProgress,
  }))
}

//#region selectors
export const selectStudyGuide = ({studyGuide}: RootState) =>
  studyGuide.studyGuide
export const selectStudyGuideStatus = ({studyGuide}: RootState) =>
  studyGuide.studyGuideStatus
//#endregion
